import type { Bookmark, Editor, TinyMCE } from 'tinymce';
import useI18nGlobal from '@/utils/i18n';
import { useEditorLink } from '../composables/useEditorLink';
import type { LinkDraft, LinkResult } from '../types/EditorLinkTypes';

const { i18n } = useI18nGlobal();
const { t } = i18n.global;

interface PendingSelection {
  bookmark: Bookmark;
}

// Bookmarks are DOM-bound and must not be made reactive, so they stay out of
// any store and are keyed by editor id instead.
const pending = new Map<string, PendingSelection>();

const ZERO_WIDTH = /﻿/g;

export function normalizeHref(raw: string): string {
  const value = (raw || '').trim();
  if (!value) return '';
  if (/^(#|\/|\.{1,2}\/)/.test(value)) return value;
  if (/^[a-z][a-z0-9.+-]*:/i.test(value)) return value;
  if (/^[^\s/@]+@[^\s/@]+\.[^\s/@]+$/.test(value)) return `mailto:${value}`;
  return `https://${value}`;
}

function findAnchor(editor: Editor): HTMLAnchorElement | null {
  const dom = editor.dom;
  const selection = editor.selection;
  const body = editor.getBody();
  const fromEdges =
    dom.getParent(selection.getStart(), 'a[href]', body) ??
    dom.getParent(selection.getEnd(), 'a[href]', body) ??
    dom.getParent(selection.getNode(), 'a[href]', body);
  if (fromEdges) return fromEdges as HTMLAnchorElement;

  // A selection can swallow a link whole together with the text around it, and
  // then neither edge sits inside the anchor.
  if (!selection.isCollapsed()) {
    const range = selection.getRng();
    const scope = selection.getNode() === body ? body : selection.getNode();
    const enclosed = dom
      .select<HTMLAnchorElement>('a[href]', scope)
      .find((anchor) => range.intersectsNode(anchor));
    if (enclosed) return enclosed;
  }
  return null;
}

function anchorText(anchor: HTMLAnchorElement): string {
  // jsdom does not implement innerText, so textContent has to carry the tests.
  return (anchor.innerText || anchor.textContent || '').replace(ZERO_WIDTH, '');
}

export function readLinkDraft(editor: Editor): LinkDraft {
  const dom = editor.dom;
  const selection = editor.selection;
  const anchor = findAnchor(editor);
  const hasSelection = !selection.isCollapsed();

  return {
    // getAttrib prefers data-mce-href, which is the URL as the author wrote it
    // rather than the one urlconverter_callback rewrote.
    href: anchor ? dom.getAttrib(anchor, 'href') : '',
    target: anchor ? dom.getAttrib(anchor, 'target') : '',
    text: anchor ? anchorText(anchor) : selection.getContent({ format: 'text' }),
    isEdit: !!anchor,
    canEditText: !hasSelection || !!anchor,
  };
}

function restoreSelection(editor: Editor): void {
  const state = pending.get(editor.id);
  pending.delete(editor.id);
  // focus() first: while the editor is unfocused it restores its own bookmark,
  // which would overwrite ours if we moved there beforehand.
  editor.focus();
  if (state) editor.selection.moveToBookmark(state.bookmark);
}

function linkAttributes(href: string, target: string): Record<string, string | null> {
  return {
    href,
    target: target || null,
    rel: target === '_blank' ? 'noopener' : null,
  };
}

function getEditor(editorId: string): Editor | null {
  const host = window as Window & { tinymce?: TinyMCE };
  return host.tinymce?.get(editorId) ?? null;
}

export function applyLink(editorId: string, result: LinkResult): void {
  const editor = getEditor(editorId);
  if (!editor) return;

  const href = normalizeHref(result.href);
  if (!href) {
    removeLink(editorId);
    return;
  }

  restoreSelection(editor);

  const dom = editor.dom;
  const selection = editor.selection;
  const anchor = findAnchor(editor);
  const attributes = linkAttributes(href, result.target);

  editor.undoManager.transact(() => {
    if (anchor) {
      if (result.text && result.text !== anchorText(anchor)) {
        anchor.textContent = result.text;
      }
      // setAttribs runs the URL through urlconverter_callback and keeps
      // data-mce-href in step; a raw setAttribute would desynchronise them.
      dom.setAttribs(anchor, attributes);
      selection.select(anchor);
    } else if (!selection.isCollapsed()) {
      editor.execCommand('mceInsertLink', false, attributes);
    } else {
      // On a collapsed cursor mceInsertLink leaves a dangling caret format
      // instead of a link, so the node has to be inserted outright.
      editor.insertContent(
        dom.createHTML('a', attributes, dom.encode(result.text || href))
      );
    }
  });
  editor.nodeChanged();
}

export function removeLink(editorId: string): void {
  const editor = getEditor(editorId);
  if (!editor) return;

  restoreSelection(editor);
  const anchor = findAnchor(editor);

  editor.undoManager.transact(() => {
    // Selecting the whole anchor first stops a partial selection from tearing
    // the link into three.
    if (anchor) editor.selection.select(anchor);
    editor.execCommand('unlink');
  });
  editor.nodeChanged();
}

export function cancelLink(editorId: string): void {
  pending.delete(editorId);
  getEditor(editorId)?.focus();
}

const FORM_KEY = 'kontextlab-link-form';

// Carried between showings of the form: what the map picker chose, and whether
// the author asked for a new window. The form itself holds only one field.
const draftHref = new Map<string, string>();
const draftNewWindow = new Map<string, boolean>();

// Applying a link leaves the caret inside the new anchor, which satisfies the
// predicate and would pop the form straight back open. Suppressed until the
// author touches the editor again.
const suppressed = new Set<string>();

// What the link looked like when the form opened, so Apply can stay disabled
// until something actually differs.
const baselineHref = new Map<string, string>();
const baselineNewWindow = new Map<string, boolean>();
// Re-runs the Apply button's enabled check; owned by that button's onSetup.
const refreshApply = new Map<string, () => void>();

function formField(): HTMLInputElement | null {
  return document.querySelector<HTMLInputElement>('.tox-pop input.tox-toolbar-textfield');
}

function hasChanges(editorId: string, current: string): boolean {
  const href = normalizeHref(current);
  const baseline = normalizeHref(baselineHref.get(editorId) ?? '');
  if (!href) return false;
  if (href !== baseline) return true;
  return (draftNewWindow.get(editorId) ?? false) !== (baselineNewWindow.get(editorId) ?? false);
}

// A context form offers no placeholder option and puts its label into
// aria-label, so the visible hint is attached once the form has rendered into
// TinyMCE's own sink.
function addFieldPlaceholder(): void {
  window.requestAnimationFrame(() => {
    document
      .querySelectorAll<HTMLInputElement>('.tox-pop input.tox-toolbar-textfield')
      .forEach((field) => {
        if (field.getAttribute('aria-label') !== t('popup.link.url')) return;
        field.setAttribute('placeholder', t('popup.link.urlPlaceholder'));
      });
  });
}

function showForm(editor: Editor): void {
  editor.dispatch('contexttoolbar-show', { toolbarKey: FORM_KEY });
  addFieldPlaceholder();
}

function hideForm(editor: Editor): void {
  editor.dispatch('contexttoolbar-hide');
}

function commit(editor: Editor, href: string): void {
  const newWindow = draftNewWindow.get(editor.id) ?? false;
  draftHref.delete(editor.id);
  draftNewWindow.delete(editor.id);
  baselineHref.delete(editor.id);
  baselineNewWindow.delete(editor.id);
  suppressed.add(editor.id);
  applyLink(editor.id, {
    href,
    text: '',
    target: newWindow ? '_blank' : '',
  });
}

const MAP_ICON =
  '<svg width="24" height="24" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill="currentColor" d="M0 117.7v346.6c0 8.8 9 14.7 17 11L160 416V96L20.1 41.1C10.7 37.4 0 44.3 0 ' +
  '54.6v63.1zM192 416l192 64V96L192 32v384zm384-352L433 105c-4.9 1.9-8 6.6-8 11.8V480l139.9-55.1c9.4-3.7 ' +
  '20.1-10.6 20.1-20.9V75.6c0-8.8-9-14.7-17-11z"/></svg>';

export default function kontextlabLink(editor: Editor): void {
  editor.ui.registry.addIcon('kontextlab-map', MAP_ICON);

  // TinyMCE dismisses the form on clicks inside the editor, but the page around
  // it — the quick edit bar, the map — is outside its reach.
  function dismissOnOutsideClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target || !document.querySelector('.tox-pop')) return;
    // A click inside the form, or inside the map overlay it opened, is not
    // "outside".
    if (target.closest('.tox-pop') || target.closest('.popup-wrapper')) return;
    hideForm(editor);
  }

  document.addEventListener('mousedown', dismissOnOutsideClick, true);

  // Opening a node parks the caret at the top of its content. If a link happens
  // to sit there the form would greet the author uninvited, so it stays
  // suppressed until they actually click or type.
  suppressed.add(editor.id);
  editor.on('keyup', () => suppressed.delete(editor.id));

  editor.on('mousedown', (event) => {
    suppressed.delete(editor.id);
    const target = event.target as Element | null;
    if (target && editor.dom.getParent(target, 'a[href]', editor.getBody())) return;
    // Clicking away from the link has to dismiss the form here rather than
    // through the document listener: the editor body lives in an iframe, so
    // those events never reach it. And while the form holds focus, a click on a
    // contenteditable="false" block — a video, an external content frame —
    // cannot move the selection at all.
    if (document.querySelector('.tox-pop')) hideForm(editor);
  });

  function rememberSelection(): void {
    // Type 2 keeps the bookmark out of the DOM; type 1 would inject marker
    // spans that leak into v-model while the form is open.
    pending.set(editor.id, { bookmark: editor.selection.getBookmark(2, true) });
  }

  editor.ui.registry.addContextForm(FORM_KEY, {
    launch: { type: 'contextformtogglebutton', icon: 'link' },
    label: t('popup.link.url'),
    // TinyMCE drives showing and hiding from this: the form appears while the
    // caret sits in a link and goes away when it leaves. Doing it by hand from
    // NodeChange fights the editor — it re-evaluates context bars after every
    // change, so the form flickers and the selection sticks to the old link.
    predicate: (node) =>
      !suppressed.has(editor.id) &&
      !!editor.dom.getParent(node, 'a[href]', editor.getBody()),
    position: 'selection',
    scope: 'editor',
    initValue: () => {
      // A carried draft means the form is coming back from the map picker, so
      // the baseline set on the first showing still stands.
      const carried = draftHref.get(editor.id);
      if (carried !== undefined) return carried;
      const draft = readLinkDraft(editor);
      draftNewWindow.set(editor.id, draft.target === '_blank');
      baselineHref.set(editor.id, draft.href);
      baselineNewWindow.set(editor.id, draft.target === '_blank');
      return draft.href;
    },
    commands: [
      {
        type: 'contextformtogglebutton',
        icon: 'new-tab',
        tooltip: t('popup.link.newWindow'),
        onSetup: (api) => {
          api.setActive(draftNewWindow.get(editor.id) ?? false);
          return () => undefined;
        },
        onAction: (formApi, api) => {
          const next = !(draftNewWindow.get(editor.id) ?? false);
          draftNewWindow.set(editor.id, next);
          api.setActive(next);
          // Keep what the author typed so far across the redraw.
          draftHref.set(editor.id, formApi.getValue());
          refreshApply.get(editor.id)?.();
        },
      },
      {
        type: 'contextformbutton',
        icon: 'kontextlab-map',
        tooltip: t('popup.link.chooseOnMap'),
        onAction: (formApi) => {
          draftHref.set(editor.id, formApi.getValue());
          formApi.hide();
          // The map overlay is a Vue component outside the editor; the form
          // steps aside and returns carrying whatever was picked.
          useEditorLink().openMapPicker((href: string) => {
            draftHref.set(editor.id, href);
            showForm(editor);
          });
        },
      },
      {
        type: 'contextformbutton',
        icon: 'unlink',
        tooltip: t('popup.link.remove'),
        onAction: (formApi) => {
          formApi.hide();
          draftHref.delete(editor.id);
          suppressed.add(editor.id);
          removeLink(editor.id);
          hideForm(editor);
        },
      },
      {
        type: 'contextformbutton',
        icon: 'checkmark',
        tooltip: t('popup.link.apply'),
        primary: true,
        // Nothing to apply until the URL or the new-window choice differs from
        // what the link already had.
        onSetup: (api) => {
          const sync = (): void =>
            api.setEnabled(hasChanges(editor.id, formField()?.value ?? ''));
          refreshApply.set(editor.id, sync);

          // The field is rendered alongside this button, so it is only reachable
          // once the frame has painted.
          let field: HTMLInputElement | null = null;
          window.requestAnimationFrame(() => {
            field = formField();
            field?.addEventListener('input', sync);
            sync();
          });

          return () => {
            field?.removeEventListener('input', sync);
            refreshApply.delete(editor.id);
          };
        },
        onAction: (formApi) => {
          const href = formApi.getValue();
          formApi.hide();
          commit(editor, href);
          hideForm(editor);
        },
      },
    ],
  });

  // Every route into the link UI — toolbar button, Insert menu, Ctrl+K, context
  // menu — funnels through this command, so intercepting it covers them all.
  // Re-registering the button instead would be silently overwritten by the
  // stock link plugin, which registers after setup() runs.
  editor.on('BeforeExecCommand', (event) => {
    if (event.command.toLowerCase() !== 'mcelink') return;
    event.preventDefault();
    rememberSelection();
    draftHref.delete(editor.id);
    // An explicit request always wins over the post-apply suppression.
    suppressed.delete(editor.id);
    showForm(editor);
  });

  editor.on('remove', () => {
    document.removeEventListener('mousedown', dismissOnOutsideClick, true);
    pending.delete(editor.id);
    draftHref.delete(editor.id);
    draftNewWindow.delete(editor.id);
    baselineHref.delete(editor.id);
    baselineNewWindow.delete(editor.id);
    suppressed.delete(editor.id);
    refreshApply.delete(editor.id);
  });
}
